/**
 * Поле `url` назовні (#1230, #1271): одне джерело для списку, картки, таблиці зв'язку й стислих
 * блоків. Посилання — лише з `externalHref` (`http(s)` без облікових даних), у новій вкладці без
 * `opener` і `Referer`. Хост — у тексті, а не лише в `title` (дотиковий екран, екранний читач):
 * IDN — у формі `xn--`, порт видно, шлях — ні; довгий хост обрізається з початку (`rtl` +
 * `<bdi dir="ltr">`), тож видно домен «…evil.test», а не піддомен (рев'ю Security #1266, S1; #1275).
 * Значення, що посиланням не стає, — текст `urlText`. Модуль малий: список бере його в першому екрані.
 */
import type { MetaField } from '@clientline/contracts';
import { shell as s } from '../i18n/uk/shell.ts';
import { externalHref, urlText } from './external-href.ts';

export { externalHref, urlText };

/** Підпис посилання з `/meta` (`params.linkLabel`, FLD-17); немає чи порожній — `null`. */
export function linkLabelOf(field: MetaField): string | null {
  const label = field.params?.linkLabel?.uk.trim();
  return label ? label : null;
}

interface ExternalLinkProps {
  /** Адреса як є: компонент сам пропускає її через `externalHref`, непридатну не показує. */
  href: string;
  /**
   * Текст посилання (`linkLabel` поля); немає — «Відкрити». Обрізається з кінця і першим віддає
   * місце хосту.
   */
  label?: string | null | undefined;
  /** Клітинка списку: лише «<хост> ↗», без `Tab` у рядок (`tabIndex=-1`, як назва). */
  compact?: boolean;
  id?: string | undefined;
  /** Доступна назва з підписом поля: `<id підпису> <id посилання>` (картка). */
  labelledBy?: string | undefined;
}

/**
 * «<Підпис> ↗ · <хост>» (картка, таблиця зв'язку, стислі блоки) чи «<хост> ↗» (список). ↗ і «·»
 * читач не вимовляє, замість них — « (відкриється в новій вкладці)»; видимий текст входить у
 * доступну назву (WCAG 2.5.3). Повна адреса — у `title`. Адресу, що посиланням не стає
 * (`externalHref` → `null`), компонент не показує: що робити з нею, вирішує викликач.
 */
export function ExternalLink({
  href: raw,
  label,
  compact = false,
  id,
  labelledBy,
}: ExternalLinkProps) {
  const href = externalHref(raw);
  if (!href) return null;
  const host = (
    <span className="ext-link__host" dir="rtl">
      <bdi dir="ltr">{new URL(href).host}</bdi>
    </span>
  );
  return (
    <a
      className={compact ? 'ext-link ext-link--compact' : 'ext-link'}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      title={href}
      {...(compact ? { tabIndex: -1 } : {})}
      {...(id ? { id } : {})}
      {...(labelledBy ? { 'aria-labelledby': labelledBy } : {})}
    >
      {compact ? (
        <>
          {host}
          <span className="ext-link__mark">
            {' '}
            <span aria-hidden="true">↗</span>
          </span>
        </>
      ) : (
        <>
          <span className="ext-link__label">{label || s.linkOpen}</span>
          {/* Пробіли — поза `aria-hidden`: інакше назва склеїть підпис з хостом («LMSlms…»). */}
          <span className="ext-link__mark">
            {' '}
            <span aria-hidden="true">↗ ·</span>{' '}
          </span>
          {host}
        </>
      )}
      <span className="sr-only"> {s.linkNewTab}</span>
    </a>
  );
}

/** Значення поля `url` за типом: посилання чи текст; інше — `null` (вирішує викликач). */
export function urlValue(
  field: MetaField,
  raw: unknown,
): { href: string } | { text: string } | null {
  if (field.type !== 'url' || typeof raw !== 'string' || raw === '') return null;
  const href = externalHref(raw);
  return href ? { href } : { text: urlText(raw) };
}
